import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Select, Spinner, Textarea } from '../ui'
import { Icon } from '../ui/icons'
import { FOLLOW_UP_STATUS, formatDate } from '../../utils/format'

export interface FollowUp {
  id: string
  suggestedDate: string
  intervalLabel?: string | null
  reason?: string | null
  notes?: string | null
  status: string
  professional?: { firstName: string; lastName: string; specialty?: string | null } | null
  service?: { id: string; name: string } | null
  appointment?: { id: string; startsAt: string; status: string } | null
}

/** Plazos que ofrece la ficha; el backend los traduce a fecha (follow-ups.routes.ts). */
const INTERVALS = [
  { key: '1w', label: '1 semana' },
  { key: '2w', label: '2 semanas' },
  { key: '4w', label: '4 semanas' },
  { key: '6w', label: '6 semanas' },
  { key: '3m', label: '3 meses' },
  { key: '6m', label: '6 meses' },
  { key: '12m', label: '12 meses' },
]

const EMPTY_FORM = { interval: '4w', reason: '', notes: '' }

/**
 * Próximos controles del paciente (P2). El profesional indica "control en 4
 * semanas" desde la ficha y eso deja de ser una frase suelta: queda como
 * pendiente de gestión para recepción y visible para el paciente en su portal.
 */
export function FollowUpsSection({
  patientId,
  encounterId,
  readOnly,
}: {
  patientId: string
  encounterId?: string
  readOnly?: boolean
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState(EMPTY_FORM)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')

  const followUps = useQuery({
    queryKey: ['follow-ups', patientId],
    queryFn: async () => (await api.get<FollowUp[]>('/follow-ups', { params: { patientId } })).data,
  })

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['follow-ups', patientId] })
  }

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post('/follow-ups', {
          patientId,
          ...(encounterId ? { encounterId } : {}),
          interval: form.interval,
          ...(form.reason ? { reason: form.reason } : {}),
          ...(form.notes ? { notes: form.notes } : {}),
        })
      ).data,
    onSuccess: () => {
      setForm(EMPTY_FORM)
      setAdding(false)
      setError('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; status?: string; suggestedDate?: string }) =>
      (await api.patch(`/follow-ups/${id}`, data)).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const list = followUps.data ?? []

  return (
    <Card
      title="Próximos controles"
      actions={
        !adding &&
        !readOnly && (
          <Button variant="secondary" onClick={() => setAdding(true)}>
            <Icon name="scheduleCheck" className="mr-1.5 inline h-4 w-4" />
            Indicar control
          </Button>
        )
      }
    >
      {adding && (
        <div className="mb-4 space-y-3 rounded-lg border border-brand-200 bg-soft p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Plazo" hint="La fecha sugerida se calcula desde hoy y se puede mover después.">
              <Select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })}>
                {INTERVALS.map((i) => (
                  <option key={i.key} value={i.key}>
                    {i.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Motivo">
              <Input
                placeholder="Control post operatorio de rodilla"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Notas internas">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex gap-2">
            <Button variant="cta" disabled={create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Guardando…' : 'Indicar control'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAdding(false)
                setError('')
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <ErrorText>{error}</ErrorText>

      {followUps.isLoading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <EmptyState message="Sin controles indicados." />
      ) : (
        <ul className="space-y-2">
          {list.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 p-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                  {f.reason ?? 'Control'}
                  <Badge className={FOLLOW_UP_STATUS[f.status]?.className}>{FOLLOW_UP_STATUS[f.status]?.label}</Badge>
                </p>
                <p className="text-xs text-gray-500">
                  Sugerido para el {formatDate(f.suggestedDate)}
                  {f.intervalLabel ? ` (${f.intervalLabel})` : ''}
                  {f.professional ? ` · ${f.professional.firstName} ${f.professional.lastName}` : ''}
                  {f.appointment ? ` · Agendado el ${formatDate(f.appointment.startsAt)}` : ''}
                </p>
              </div>
              {!readOnly && ['PENDING', 'SCHEDULED'].includes(f.status) && (
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" onClick={() => update.mutate({ id: f.id, status: 'DONE' })}>
                    Marcar realizado
                  </Button>
                  <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => update.mutate({ id: f.id, status: 'CANCELLED' })}>
                    Anular
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
